<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import { DATE_RANGE_OPTIONS } from './constants';

const { t } = useI18n();

const SUPPORTED_RANGE_IDS = [
  DATE_RANGE_OPTIONS.TODAY.id,
  DATE_RANGE_OPTIONS.YESTERDAY.id,
  DATE_RANGE_OPTIONS.LAST_7_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_30_DAYS.id,
  DATE_RANGE_OPTIONS.LAST_3_MONTHS.id,
  DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id,
];

const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const isBreakdownOpen = ref(true);

const filters = ref({
  from: 0,
  to: 0,
  userIds: [],
  labelId: null,
  inboxId: null,
  teamId: null,
  vipLevelId: null,
  issueId: null,
  priority: null,
});

const summary = ref({
  current_period: {},
  previous_period: {},
  current: {
    chat_duration_seconds: 0,
    agent_chatting_time_seconds: 0,
  },
  previous: {
    chat_duration_seconds: 0,
    agent_chatting_time_seconds: 0,
  },
});

const series = ref([]);

const formatDuration = totalSeconds => {
  const seconds = Math.max(Math.round(Number(totalSeconds || 0)), 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}min`;
  }
  return `${minutes}min ${remainingSeconds}s`;
};

const formatDurationDiff = totalSeconds => {
  const numericValue = Number(totalSeconds || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${formatDuration(Math.abs(numericValue))}`;
};

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const currentPeriodLabel = computed(() => {
  const period = summary.value.current_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const previousPeriodLabel = computed(() => {
  const period = summary.value.previous_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const chartRows = computed(() =>
  (series.value || []).map(row => {
    const currentDateLabel = format(
      fromUnixTime(row.current_timestamp),
      'dd MMM yy'
    );
    const previousDateLabel = format(
      fromUnixTime(row.previous_timestamp),
      'dd MMM yy'
    );

    return {
      ...row,
      currentDateLabel,
      previousDateLabel,
      chatDurationDiff:
        Number(row.current_chat_duration_seconds || 0) -
        Number(row.previous_chat_duration_seconds || 0),
      agentChattingDiff:
        Number(row.current_agent_chatting_seconds || 0) -
        Number(row.previous_agent_chatting_seconds || 0),
    };
  })
);

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row =>
    format(fromUnixTime(row.current_timestamp), 'dd MMM')
  ),
  datasets: [
    {
      label: `${t('CHAT_DURATION_REPORT.CHAT_DURATION')} (${currentPeriodLabel.value})`,
      data: chartRows.value.map(row =>
        Number(row.current_chat_duration_seconds || 0)
      ),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
    {
      label: `${t('CHAT_DURATION_REPORT.CHAT_DURATION')} (${previousPeriodLabel.value})`,
      data: chartRows.value.map(row =>
        Number(row.previous_chat_duration_seconds || 0)
      ),
      backgroundColor: '#E7C51B',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
    {
      label: `${t('CHAT_DURATION_REPORT.AGENT_CHATTING_TIME')} (${currentPeriodLabel.value})`,
      data: chartRows.value.map(row =>
        Number(row.current_agent_chatting_seconds || 0)
      ),
      backgroundColor: '#C23881',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
    {
      label: `${t('CHAT_DURATION_REPORT.AGENT_CHATTING_TIME')} (${previousPeriodLabel.value})`,
      data: chartRows.value.map(row =>
        Number(row.previous_agent_chatting_seconds || 0)
      ),
      backgroundColor: '#F97316',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
  ],
}));

const chartOptions = {
  plugins: {
    legend: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => `${Math.round(Number(value) / 60)} min`,
      },
    },
  },
};

const diffClass = value => {
  if (value > 0) return 'text-n-teal-10';
  if (value < 0) return 'text-n-ruby-9';
  return 'text-n-slate-11';
};

const fetchReport = async () => {
  if (!filters.value.from || !filters.value.to) return;

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getChatDuration({
      from: filters.value.from,
      to: filters.value.to,
      userIds: filters.value.userIds,
      labelId: filters.value.labelId,
      inboxId: filters.value.inboxId,
      teamId: filters.value.teamId,
      vipLevelId: filters.value.vipLevelId,
      issueId: filters.value.issueId,
      priority: filters.value.priority,
    });

    summary.value = response.data?.summary || summary.value;
    series.value = response.data?.series || [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('CHAT_DURATION_REPORT.FETCH_ERROR');
    series.value = [];
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({
  from,
  to,
  selectedAgents = [],
  selectedLabel = null,
  selectedInbox = null,
  selectedTeam = null,
  selectedVip = null,
  selectedIssue = null,
  selectedPriority = null,
}) => {
  filters.value = {
    from,
    to,
    userIds: Array.isArray(selectedAgents)
      ? selectedAgents.map(agent => agent.id)
      : [],
    labelId: selectedLabel?.id || null,
    inboxId: selectedInbox?.id || null,
    teamId: selectedTeam?.id || null,
    vipLevelId: selectedVip?.id || null,
    issueId: selectedIssue?.id || null,
    priority: selectedPriority?.id || null,
  };
  fetchReport();
};

const downloadBreakdownCsv = () => {
  const headers = [
    t('CHAT_DURATION_REPORT.SERIES'),
    ...chartRows.value.map(
      row =>
        `${row.currentDateLabel} ${t('CHAT_DURATION_REPORT.VS')} ${row.previousDateLabel}`
    ),
  ];

  const rows = [
    [
      t('CHAT_DURATION_REPORT.CHAT_DURATION'),
      ...chartRows.value.map(
        row =>
          `${formatDuration(row.current_chat_duration_seconds)} | ${formatDuration(
            row.previous_chat_duration_seconds
          )} | ${formatDurationDiff(row.chatDurationDiff)}`
      ),
    ],
    [
      t('CHAT_DURATION_REPORT.AGENT_CHATTING_TIME'),
      ...chartRows.value.map(
        row =>
          `${formatDuration(row.current_agent_chatting_seconds)} | ${formatDuration(
            row.previous_agent_chatting_seconds
          )} | ${formatDurationDiff(row.agentChattingDiff)}`
      ),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'chat-duration-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);
</script>

<template>
  <div class="cd-page">
    <div class="cd-shell">
      <div class="cd-header">
        <div class="cd-header-left">
          <h1 class="cd-title">{{ t('CHAT_DURATION_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="cd-toolbar">
        <div class="cd-toolbar-left">
          <CompactDateInboxFilters
            :allowed-date-range-ids="SUPPORTED_RANGE_IDS"
            show-agents
            show-labels
            show-teams
            show-vip
            show-issue
            show-priority
            hide-inactive-filters
            @filter-change="onFilterChange"
          />
        </div>
      </div>

      <div class="cd-content">
        <div v-if="errorMessage" class="cd-error">
          {{ errorMessage }}
        </div>

        <section class="cd-card">
          <div class="cd-card-header">
            <div class="cd-card-title-wrap">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                />
              </svg>
              <h2 class="cd-card-title">
                {{ t('CHAT_DURATION_REPORT.AVERAGE_SECTION_TITLE') }}
              </h2>
              <span class="cd-period">{{ currentPeriodLabel }}</span>
            </div>

            <button
              class="cd-export-btn"
              type="button"
              @click="downloadBreakdownCsv"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                />
              </svg>
              <span>{{ t('CHAT_DURATION_REPORT.EXPORT_CSV') }}</span>
            </button>
          </div>

          <div
            class="cd-section"
            :class="{ 'cd-section--no-bottom': !!chartRows.length }"
          >
            <div class="cd-summary-grid">
              <div class="cd-summary-card">
                <p class="cd-summary-label">
                  {{ t('CHAT_DURATION_REPORT.CHAT_DURATION') }}
                </p>
                <p class="cd-summary-sub">{{ currentPeriodLabel }}</p>
                <div class="cd-summary-value">
                  {{ formatDuration(summary.current.chat_duration_seconds) }}
                </div>
              </div>
              <div class="cd-summary-card">
                <p class="cd-summary-label">
                  {{ t('CHAT_DURATION_REPORT.CHAT_DURATION') }}
                </p>
                <p class="cd-summary-sub">{{ previousPeriodLabel }}</p>
                <div class="cd-summary-value">
                  {{ formatDuration(summary.previous.chat_duration_seconds) }}
                </div>
              </div>
              <div class="cd-summary-card">
                <p class="cd-summary-label">
                  {{ t('CHAT_DURATION_REPORT.AGENT_CHATTING_TIME') }}
                </p>
                <p class="cd-summary-sub">{{ currentPeriodLabel }}</p>
                <div class="cd-summary-value">
                  {{
                    formatDuration(summary.current.agent_chatting_time_seconds)
                  }}
                </div>
              </div>
              <div class="cd-summary-card">
                <p class="cd-summary-label">
                  {{ t('CHAT_DURATION_REPORT.AGENT_CHATTING_TIME') }}
                </p>
                <p class="cd-summary-sub">{{ previousPeriodLabel }}</p>
                <div class="cd-summary-value">
                  {{
                    formatDuration(summary.previous.agent_chatting_time_seconds)
                  }}
                </div>
              </div>
            </div>

            <div class="cd-chart">
              <woot-loading-state
                v-if="isLoading"
                :message="$t('REPORT.LOADING_CHART')"
                class="h-full"
              />
              <div v-else-if="!chartRows.length" class="cd-empty">
                {{ t('CHAT_DURATION_REPORT.NO_DATA') }}
              </div>
              <BarChart
                v-else
                :collection="chartCollection"
                :chart-options="chartOptions"
              />
            </div>
          </div>

          <div class="cd-breakdown">
            <div
              class="cd-breakdown-header"
              role="button"
              tabindex="0"
              @click="isBreakdownOpen = !isBreakdownOpen"
              @keydown.enter.prevent="isBreakdownOpen = !isBreakdownOpen"
              @keydown.space.prevent="isBreakdownOpen = !isBreakdownOpen"
            >
              <div class="cd-breakdown-title">
                <svg
                  class="cd-breakdown-caret"
                  :class="{ 'is-collapsed': !isBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{ t('CHAT_DURATION_REPORT.BREAKDOWN_TITLE') }}</span>
              </div>
            </div>

            <div v-show="isBreakdownOpen" class="cd-table-wrap">
              <table class="cd-table">
                <thead>
                  <tr>
                    <th class="cd-th-series">
                      {{ t('CHAT_DURATION_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="row in chartRows"
                      :key="`duration-header-${row.current_timestamp}`"
                      class="cd-th-data"
                    >
                      <div>{{ row.currentDateLabel }}</div>
                      <div class="cd-th-sub">
                        {{ t('CHAT_DURATION_REPORT.VS') }}
                        {{ row.previousDateLabel }}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="cd-td-series">
                      {{ t('CHAT_DURATION_REPORT.CHAT_DURATION') }}
                    </td>
                    <td
                      v-for="row in chartRows"
                      :key="`duration-chat-${row.current_timestamp}`"
                      class="cd-td-data"
                    >
                      <div class="cd-cell-main">
                        {{ formatDuration(row.current_chat_duration_seconds) }}
                      </div>
                      <div class="cd-cell-sub">
                        {{ formatDuration(row.previous_chat_duration_seconds) }}
                      </div>
                      <div
                        class="cd-cell-diff"
                        :class="diffClass(row.chatDurationDiff)"
                      >
                        {{ formatDurationDiff(row.chatDurationDiff) }}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td class="cd-td-series">
                      {{ t('CHAT_DURATION_REPORT.AGENT_CHATTING_TIME') }}
                    </td>
                    <td
                      v-for="row in chartRows"
                      :key="`duration-agent-${row.current_timestamp}`"
                      class="cd-td-data"
                    >
                      <div class="cd-cell-main">
                        {{ formatDuration(row.current_agent_chatting_seconds) }}
                      </div>
                      <div class="cd-cell-sub">
                        {{
                          formatDuration(row.previous_agent_chatting_seconds)
                        }}
                      </div>
                      <div
                        class="cd-cell-diff"
                        :class="diffClass(row.agentChattingDiff)"
                      >
                        {{ formatDurationDiff(row.agentChattingDiff) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="cd-footer-note">
          {{
            t('CHAT_DURATION_REPORT.GENERATED_AT', { time: generatedAtLabel })
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cd-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.cd-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.cd-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}
.cd-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.cd-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.cd-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 3px 10px -6px;
}
.cd-toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.cd-content {
  padding: 16px 32px;
}
.cd-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.cd-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #fff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}
.cd-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}
.cd-card-title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cd-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}
.cd-period {
  font-size: 12px;
  color: #6b7280;
  margin-left: 10px;
  white-space: nowrap;
}

.cd-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.cd-export-btn:hover {
  background: #f9fafb;
}

.cd-section {
  padding: 16px 20px 20px;
}
.cd-section--no-bottom {
  padding-bottom: 0;
}

.cd-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .cd-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.cd-summary-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.cd-summary-label {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.cd-summary-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
}
.cd-summary-value {
  margin-top: 10px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #111827;
}

.cd-chart {
  height: 384px;
}
.cd-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.cd-breakdown {
  border-top: 1px solid #dfe3e8;
}
.cd-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}
.cd-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.cd-breakdown-caret {
  transition: transform 0.18s ease;
}
.cd-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.cd-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}
.cd-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: #424d57;
}
.cd-th-series {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #111827;
}
.cd-th-data {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
}
.cd-th-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.cd-td-series {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  border-right: 1px solid #edf1f5;
  padding: 12px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #edf1f5;
}
.cd-td-data {
  padding: 10px 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: top;
}
.cd-cell-main {
  font-weight: 700;
  color: #111827;
}
.cd-cell-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}
.cd-cell-diff {
  margin-top: 2px;
  font-size: 11px;
  font-weight: 700;
}

.cd-footer-note {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

::global(body.dark) .cd-page,
::global(body.dark) .cd-shell,
::global(body.dark) .cd-card,
::global(body.dark) .cd-header,
::global(body.dark) .cd-toolbar,
::global(body.dark) .cd-th-series,
::global(body.dark) .cd-th-data,
::global(body.dark) .cd-td-series,
::global(body.dark) .cd-td-data,
::global(body.theme-mix) .cd-page,
::global(body.theme-mix) .cd-shell,
::global(body.theme-mix) .cd-card,
::global(body.theme-mix) .cd-header,
::global(body.theme-mix) .cd-toolbar,
::global(body.theme-mix) .cd-th-series,
::global(body.theme-mix) .cd-th-data,
::global(body.theme-mix) .cd-td-series,
::global(body.theme-mix) .cd-td-data {
  background: #fff !important;
  color: #111827 !important;
}

::global(body.dark) .cd-th-series *,
::global(body.dark) .cd-th-data *,
::global(body.theme-mix) .cd-th-series *,
::global(body.theme-mix) .cd-th-data * {
  color: #111827 !important;
}
</style>
